/**
 * Ponto de entrada: decide entre login e painel, e liga as pecas.
 *
 * Responsabilidade: ordem de boot, troca de tela, e registro do service worker.
 *
 * NAO e responsabilidade deste arquivo: regra de negocio de qualquer especie.
 *
 * ## A ordem do boot e o gate desta fase
 *
 * Cache PRIMEIRO, rede depois. O painel e desenhado com o que estava em disco
 * antes de qualquer `fetch` sair — e por isso que, em modo aviao, o app abre
 * com dado velho em vez de tela branca ou de um spinner que nunca resolve.
 * Na ordem inversa (esperar `/v1/auth/me` para so entao desenhar), o mesmo
 * codigo ficaria travado ate o timeout do TCP, que num celular sem sinal pode
 * ser mais de um minuto.
 *
 * O que NAO e restaurado do disco e a credencial: o access token vive so em
 * memoria (ver `api.ts`). Offline, portanto, o app mostra dado velho e nao
 * finge ter sessao — a primeira coisa que ele faz quando a rede volta e
 * `POST /v1/auth/refresh`.
 */

import * as acoes from "./acoes";
import * as api from "./api";
import { SemRede, SemSessao } from "./api";
import * as push from "./push";
import * as sessao from "./sessao";
import * as store from "./store";
import "./style.css";
import * as appView from "./views/app";
import * as loginView from "./views/login";
import * as ws from "./ws";

const raiz = document.querySelector<HTMLElement>("#app");
if (raiz === null) throw new Error("#app não existe no shell");

let destruirApp: (() => void) | null = null;

function trocarTela(no: HTMLElement): void {
  destruirApp?.();
  destruirApp = null;
  raiz?.replaceChildren(no);
}

function mostrarLogin(): void {
  ws.parar();
  store.encerrarSessao();
  trocarTela(loginView.montar(() => void entrar()));
}

function mostrarPainel(): void {
  if (destruirApp !== null) return; // ja montado — nao remontar a cada quadro
  const montado = appView.montar(() => void sair());
  destruirApp = montado.destruir;
  raiz?.replaceChildren(montado.raiz);
}

async function sair(): Promise<void> {
  ws.parar();
  await api.sair().catch(() => {
    // Logout sem rede: o refresh continua valido no servidor ate expirar. O
    // que da para garantir daqui e apagar o que este navegador guardou, e e
    // isso que `mostrarLogin` faz.
  });
  mostrarLogin();
}

/** Depois de login/cadastro bem-sucedido, ou no boot com sessao valida. */
async function entrar(): Promise<void> {
  mostrarPainel();
  ws.iniciar(sessao.encerrar);
  const usuario = await acoes.executar("carregar conta", () => api.eu());
  if (usuario !== null) store.definirSessao(usuario);
  carregarDoUsuario();
}

/**
 * O que so faz sentido buscar com sessao no ar.
 *
 * O feed vem por REST porque o relay so entrega o que acontecer com a aba ja
 * aberta — mesmo padrao dos comandos e dos uploads.
 *
 * `push.sincronizar()` NAO esta aqui: ele nao fala com a nuvem, so le o
 * navegador. Ver o `boot()`.
 */
function carregarDoUsuario(): void {
  void acoes.carregarAlertas();
  void acoes.carregarPrefsNotificacao();
}

async function boot(): Promise<void> {
  sessao.definirEncerramento(mostrarLogin);
  acoes.acompanharSelecao();
  acoes.acompanharConexao();
  // Fora de qualquer caminho de rede, de proposito: "este navegador consegue
  // receber notificacao?" e "ja tem inscricao?" sao perguntas para o proprio
  // navegador, e a resposta vale igual em modo aviao. Enquanto isto morava
  // junto das buscas de rede, um boot OFFLINE saia pelo ramo do `SemRede` antes
  // de chegar aqui e a tela dizia "este navegador nao recebe notificacoes" pelo
  // resto da sessao — em um navegador que recebe. Achado do gate da C9.
  void push.sincronizar();
  acoes.acompanharGrafico();
  acoes.acompanharTimelapse();

  // 1. Disco. Se havia sessao lembrada, a tela ja aparece — com etiqueta de
  //    velho, cuidada pela barra de estado do `views/app.ts`.
  const tinhaSessao = store.restaurarDoCache();
  if (tinhaSessao) mostrarPainel();

  // 2. Rede.
  try {
    const usuario = await api.eu();
    store.definirSessao(usuario);
    mostrarPainel();
    ws.iniciar(sessao.encerrar);
    carregarDoUsuario();
  } catch (erro) {
    if (erro instanceof SemRede) {
      // Offline. Com cache, fica o painel velho e etiquetado; sem cache, o
      // login — que tambem vai falhar, e vai dizer por que.
      store.definirConexao("sem-rede");
      if (!tinhaSessao) mostrarLogin();
      else ws.iniciar(sessao.encerrar); // volta sozinho quando a rede voltar
      return;
    }
    if (erro instanceof SemSessao) {
      mostrarLogin();
      return;
    }
    mostrarLogin();
  }
}

// Grava antes de a aba sumir: o `setTimeout` de persistencia do store pode
// nunca chegar a rodar quando o sistema mata a aba em segundo plano, e e
// justamente essa aba que o usuario reabre offline depois.
window.addEventListener("pagehide", () => store.persistir());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") store.persistir();
});

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  // Depois do `load`: registrar durante o boot disputa banda com o proprio
  // bundle que a tela precisa agora.
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js");
  });
}

void boot();
